import { Link } from "react-router-dom";
import { ArrowRight, Code, Server, Wrench, Globe, Shield, Zap, Users, CheckCircle, Star, MonitorSmartphone, Database, Cloud } from "lucide-react";
import { Badge } from "../components/ui/badge";

const stats = [
  { value: "20+", label: "Web Applications Built" },
  { value: "50+", label: "Websites Delivered" },
  { value: "3+", label: "Years of Excellence" },
  { value: "99.9%", label: "Uptime Guarantee" },
];

const services = [
  {
    icon: Code,
    title: "Software Development",
    description: "Custom web applications, enterprise solutions, and scalable platforms built with modern tech stacks.",
    accent: "indigo",
  },
  {
    icon: Cloud,
    title: "Application Hosting",
    description: "Secure, high-performance cloud hosting with 99.9% uptime, automated backups, and seamless scaling.",
    accent: "cyan",
  },
  {
    icon: Wrench,
    title: "Maintenance & Support",
    description: "Ongoing application maintenance, performance optimization, security patches, and 24/7 monitoring.",
    accent: "indigo",
  },
  {
    icon: MonitorSmartphone,
    title: "UI/UX Design",
    description: "Intuitive, responsive interfaces that enhance user engagement and drive business outcomes.",
    accent: "cyan",
  },
  {
    icon: Database,
    title: "Database Solutions",
    description: "Efficient database architecture, migration services, and performance-tuned data management.",
    accent: "indigo",
  },
  {
    icon: Shield,
    title: "Security & Compliance",
    description: "Application security audits, vulnerability assessments, and compliance-ready implementations.",
    accent: "cyan",
  },
];

const clients = [
  { name: "TheFirm26", initial: "TF" },
  { name: "U&G Developers", initial: "UG" },
];

const testimonials = [
  {
    quote: "Scriptovative delivered our platform ahead of schedule with exceptional quality. Their team's technical depth is remarkable.",
    author: "TheFirm26",
    role: "Client Partner",
  },
  {
    quote: "Reliable hosting and proactive maintenance — we haven't had a single minute of unplanned downtime since partnering with Scriptovative.",
    author: "U&G Developers",
    role: "Client Partner",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1771773490670-7376a45c0e96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwZGlnaXRhbCUyMG5ldHdvcmslMjBjb25uZWN0aW9uJTIwYmFja2dyb3VuZCUyMGJsdWV8ZW58MHx8fHwxNzcyMDk4OTUyfDA&ixlib=rb-4.1.0&q=85"
            alt="Digital network background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/80 to-slate-950" />
        </div>

        {/* Decorative Grid */}
        <div className="absolute inset-0 grid-pattern opacity-40" />

        {/* Decorative Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 px-4 py-1.5 text-xs font-mono tracking-wider" data-testid="hero-badge">
              ENGINEERING DIGITAL EXCELLENCE
            </Badge>

            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-7xl font-bold text-slate-50 leading-[1.1] tracking-tight animate-fade-in-up">
              Code that Builds{" "}
              <span className="gradient-text">Empires.</span>
            </h1>

            <p className="mt-6 text-base lg:text-lg text-slate-400 leading-relaxed max-w-xl animate-fade-in-up delay-200" style={{ animationDelay: "200ms" }}>
              Scriptovative Technologies delivers end-to-end software development, 
              reliable application hosting, and proactive maintenance — backed by an 
              expertise of developing more than 20+ web applications and numerous websites.
            </p>

            <div className="mt-10 flex flex-wrap gap-4 animate-fade-in-up" style={{ animationDelay: "400ms" }}>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:-translate-y-0.5"
              >
                Explore Services
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="inline-flex items-center gap-2 bg-transparent border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 px-8 py-3.5 rounded-full font-medium transition-all hover:-translate-y-0.5"
              >
                View Products
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-slate-500 rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="relative py-20 border-y border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center group" data-testid={`stat-${i}`}>
                <div className="font-['Outfit'] text-3xl lg:text-5xl font-bold gradient-text mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 hover:bg-cyan-500/10 text-xs font-mono" data-testid="services-badge">
              OUR SERVICES
            </Badge>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50 tracking-tight">
              Everything you need to{" "}
              <span className="gradient-text">ship & scale.</span>
            </h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">
              From concept to deployment to long-term maintenance — we handle the 
              full lifecycle of your software products.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <div
                key={i}
                data-testid={`service-card-${i}`}
                className="tracing-beam p-8 rounded-2xl bg-gradient-to-b from-slate-800/50 to-slate-900/50 border border-slate-800 hover:border-indigo-500/30 group relative overflow-hidden transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${
                  service.accent === "cyan"
                    ? "bg-cyan-500/10 text-cyan-400"
                    : "bg-indigo-500/10 text-indigo-400"
                }`}>
                  <service.icon className="w-6 h-6" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold text-slate-100 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {service.description}
                </p>
                <div className={`absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-500 opacity-0 group-hover:opacity-100 ${
                  service.accent === "cyan" ? "bg-cyan-500" : "bg-indigo-500"
                }`} />
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/services"
              data-testid="services-view-all"
              className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 font-medium transition-colors"
            >
              View all services in detail
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-us-section" className="relative py-24 lg:py-32 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 text-xs font-mono">
                WHY SCRIPTOVATIVE
              </Badge>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
                Built by developers,{" "}
                <span className="gradient-text">for developers.</span>
              </h2>
              <p className="mt-4 text-base text-slate-400 leading-relaxed">
                We don't just write code — we architect solutions. Our team brings 
                deep expertise across the full technology stack to deliver software 
                that performs, scales, and lasts.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Agile methodology with transparent project tracking",
                  "Modern tech stack: React, Node.js, Python, Cloud-native",
                  "Dedicated project managers for every engagement",
                  "Post-launch support and performance monitoring",
                  "20+ successful web application deliveries",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300 text-sm">
                    <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-slate-800">
                <img
                  src="https://images.unsplash.com/photo-1649451844813-3130d6f42f8a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxzb2Z0d2FyZSUyMGNvZGUlMjBzY3JlZW4lMjBkYXJrJTIwbW9kZSUyMGFic3RyYWN0fGVufDB8fHx8MTc3MjA5ODk1MHww&ixlib=rb-4.1.0&q=85"
                  alt="Software development"
                  className="w-full h-80 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-slate-900/90 backdrop-blur-md border border-slate-800 rounded-xl p-5 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <div className="font-['Outfit'] font-semibold text-slate-100 text-sm">Fast Delivery</div>
                    <div className="text-slate-400 text-xs">Avg. 40% faster than industry</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section data-testid="clients-section" className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 hover:bg-cyan-500/10 text-xs font-mono">
              TRUSTED PARTNERS
            </Badge>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              Companies we've helped{" "}
              <span className="gradient-text">grow.</span>
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-8 mb-16">
            {clients.map((client, i) => (
              <div
                key={i}
                data-testid={`client-card-${i}`}
                className="flex items-center gap-4 px-8 py-5 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-indigo-500/30 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                  <span className="font-['Outfit'] font-bold text-indigo-400 text-lg">{client.initial}</span>
                </div>
                <span className="font-['Outfit'] font-semibold text-slate-200 text-lg">{client.name}</span>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="p-8 rounded-2xl bg-slate-900/50 border border-slate-800 relative"
              >
                <Star className="w-8 h-8 text-indigo-500/30 mb-4" />
                <p className="text-slate-300 text-sm leading-relaxed italic mb-6">
                  "{t.quote}"
                </p>
                <div>
                  <div className="font-['Outfit'] font-semibold text-slate-200 text-sm">{t.author}</div>
                  <div className="text-slate-500 text-xs">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vidyora Teaser */}
      <section data-testid="vidyora-teaser" className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-transparent to-orange-500/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-orange-500/10 text-orange-400 border-orange-500/20 hover:bg-orange-500/10 text-xs font-mono">
                OUR PRODUCT
              </Badge>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
                Meet{" "}
                <span className="gradient-text-orange">Vidyora</span>
              </h2>
              <p className="mt-4 text-base text-slate-400 leading-relaxed">
                A comprehensive school management system designed to streamline 
                administration, enhance communication, and empower educators with 
                powerful digital tools.
              </p>
              <Link
                to="/vidyora"
                data-testid="vidyora-learn-more"
                className="mt-8 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(249,115,22,0.3)] hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] hover:-translate-y-0.5"
              >
                Learn More
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-orange-500/20 shadow-[0_0_40px_rgba(249,115,22,0.1)]">
                <img
                  src="https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Vidyora dashboard"
                  className="w-full h-72 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="relative py-24 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-t from-indigo-600/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50 tracking-tight">
            Ready to build something{" "}
            <span className="gradient-text">extraordinary?</span>
          </h2>
          <p className="mt-6 text-base text-slate-400 leading-relaxed max-w-xl mx-auto">
            Let's discuss your project. From ideation to deployment, 
            we'll architect the perfect solution for your business.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="cta-contact"
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:-translate-y-0.5"
            >
              Start a Project
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/about"
              data-testid="cta-about"
              className="inline-flex items-center gap-2 bg-transparent border border-slate-700 text-slate-300 hover:bg-slate-800/50 px-8 py-3.5 rounded-full font-medium transition-all hover:-translate-y-0.5"
            >
              Learn About Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
